import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { ButtonRounded } from '../UISystem/Buttons';
import { SHADOWS } from '../../styles/UISystem';
import { uploadPickedPhoto } from '../../services/storage/photoUpload';

/**
 * QrPhotoSheet — the photo pitch on the QR screen (replaces QRCustomizeOverlay
 * for Simple profiles, user design 2026-08-16).
 *
 * Why it lives HERE and not on the People page: interposing a sheet between
 * "Make QR" and the QR read as a toll gate on the thing they just asked for
 * (device pass, 2026-08-16 — "a little odd"). The QR lands first, works
 * immediately, and the sheet is an invitation to make it better.
 *
 * "Add photo" opens the iOS picker DIRECTLY — one tap from sheet to camera
 * roll, no intermediate chooser. "Later" is a real escape, one tap, no
 * penalty: the gradient QR behind this sheet is already connectable.
 *
 * No scrim — elevation plus an invisible tap-outside layer, per
 * ui-design-system §4.8 (a backdrop tints Safari's toolbars).
 */
const QrPhotoSheet = ({ show, onPhotoSelected, onDismiss }) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Body scroll lock while the sheet is open (ui-design-system §4.7)
  useEffect(() => {
    if (!show) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file || busy) return;
    setBusy(true);
    try {
      const photoUrl = await uploadPickedPhoto(file);
      // A failed photo must not cost them the sheet's exit — dismiss either way.
      if (photoUrl) onPhotoSelected(photoUrl);
      else onDismiss();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          <div
            onClick={onDismiss}
            style={{ position: 'fixed', inset: 0, zIndex: 1999, background: 'transparent' }}
          />

          <motion.div
            initial={{ y: 400 }}
            animate={{ y: 0 }}
            exit={{ y: 400 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2000,
              backgroundColor: 'white',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              boxShadow: SHADOWS.TIER3,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '28px 24px',
                paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
                maxWidth: '340px',
                margin: '0 auto',
              }}
            >
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#000000' }}>
                Make this QR interesting
              </div>
              <div style={{ fontSize: '17px', color: '#000000', marginTop: '10px' }}>
                Customize with your profile photo
              </div>

              <div style={{ marginTop: '22px' }}>
                <ButtonRounded
                  text={busy ? 'Adding…' : 'Add photo'}
                  onClick={() => fileInputRef.current?.click()}
                  variant="primary"
                  size="auto"
                  disabled={busy}
                  style={{ minWidth: '210px' }}
                />
              </div>

              <div style={{ marginTop: '20px' }}>
                <ButtonRounded
                  text="Later"
                  onClick={onDismiss}
                  variant="blank"
                  size="inline"
                  disabled={busy}
                />
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

QrPhotoSheet.propTypes = {
  show: PropTypes.bool,
  onPhotoSelected: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default QrPhotoSheet;
